import React, { useState } from 'react';
import { Drawer, Tooltip } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useAgentTraceDetail } from '../../hooks/useAgentAnalytics';
import { getEvaluatorDescription, getEvaluatorLabel } from '@troia-v3/types';

interface TraceDetailDrawerProps {
  agentId: string;
  traceId: string | null;
  onClose: () => void;
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(value);
}

function formatText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export const TraceDetailDrawer: React.FC<TraceDetailDrawerProps> = ({
  agentId,
  traceId,
  onClose,
}) => {
  const { data: trace, isLoading } = useAgentTraceDetail(
    agentId,
    traceId ?? undefined,
  );

  // Copy do sessionId (badge de identidade da trace) — ícone copy → check por 2s,
  // sem toast (mesmo padrão do TicketDetailHeader variant drawer).
  const [copiedSessionId, setCopiedSessionId] = useState(false);
  const handleCopySessionId = async (): Promise<void> => {
    if (!trace) return;
    try {
      await navigator.clipboard.writeText(trace.sessionId);
      setCopiedSessionId(true);
      setTimeout(() => setCopiedSessionId(false), 2000);
    } catch {
      // Fallback silencioso
    }
  };

  if (!traceId) return null;

  // Drawer shared: portal, z-[60], Esc, animação de entrada, tema claro forçado
  // e mobile full-width vêm do primitivo (DS §Overlays). `size="lg"` = 720px.
  return (
    <Drawer
      isOpen
      onClose={onClose}
      size="lg"
      icon={<Icon name="activity" className="w-5 h-5" />}
      title="Detalhes da conversa"
      subtitle={
        trace ? new Date(trace.timestamp).toLocaleString('pt-BR') : undefined
      }
    >
      {/* Padding é responsabilidade do consumidor (Drawer não aplica) */}
      <div className="p-6 space-y-4">
        {isLoading && (
          <div className="text-sm" style={{ color: '#6b7280' }}>
            Carregando...
          </div>
        )}

        {trace && (
          <>
            {/* Identidade da trace — Session ID em badge pill mono com
                copy-to-clipboard (padrão TicketDetailHeader, DS 2026-08-11) */}
            <div className="flex items-center min-w-0">
              <span
                className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-full text-xs font-bold font-mono max-w-full min-w-0"
                style={{ backgroundColor: '#f1f5f9', color: '#334155' }}
                data-testid="trace-detail-session-id"
              >
                <Icon name="hash" className="w-3 h-3 flex-none" />
                <span className="truncate">{trace.sessionId}</span>
                {/* Tooltip shared (Radix) — NUNCA `title=` nativo (DS F80) */}
                <Tooltip content="Copiar Session ID">
                  <button
                    type="button"
                    onClick={() => {
                      void handleCopySessionId();
                    }}
                    className="p-1 rounded-full transition-colors flex-none"
                    style={{ color: copiedSessionId ? '#059669' : '#64748b' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e2e8f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    aria-label="Copiar Session ID"
                    data-testid="trace-detail-session-id-copy"
                  >
                    <Icon
                      name={copiedSessionId ? 'check' : 'copy'}
                      className="w-3 h-3"
                    />
                  </button>
                </Tooltip>
              </span>
            </div>

            {/* Metadata summary */}
            <div
              className="rounded-lg border p-3 grid grid-cols-2 gap-2 text-sm"
              style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb' }}
            >
              <div>
                <div className="text-xs" style={{ color: '#6b7280' }}>
                  Latência
                </div>
                <div className="font-medium" style={{ color: '#1e293b' }}>
                  {trace.latencySeconds.toFixed(2)}s
                </div>
              </div>
              <div>
                <div className="text-xs" style={{ color: '#6b7280' }}>
                  Custo
                </div>
                <div className="font-medium" style={{ color: '#1e293b' }}>
                  {formatUsd(trace.costUsd)}
                </div>
              </div>
            </div>

            {/* Scores */}
            {trace.scores.length > 0 && (
              <div
                className="rounded-lg border p-3"
                style={{ borderColor: '#e5e7eb' }}
              >
                <div
                  className="text-xs font-medium mb-2"
                  style={{ color: '#6b7280' }}
                >
                  Avaliações
                </div>
                <div className="space-y-1.5">
                  {trace.scores.map((score, i) => {
                    const description = getEvaluatorDescription(score.name);
                    const row = (
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#334155' }}>
                          {getEvaluatorLabel(score.name)}
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: '#1e293b' }}
                        >
                          {Math.round(score.value * 100)}%
                        </span>
                      </div>
                    );
                    // Tooltip shared (Radix) — NUNCA `title=` nativo (DS F80)
                    return description ? (
                      <Tooltip key={i} content={description}>
                        {row}
                      </Tooltip>
                    ) : (
                      <React.Fragment key={i}>{row}</React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Input */}
            <div>
              <div
                className="text-xs font-medium mb-1"
                style={{ color: '#6b7280' }}
              >
                Input
              </div>
              <pre
                className="text-xs p-3 rounded border overflow-x-auto whitespace-pre-wrap"
                style={{
                  borderColor: '#e5e7eb',
                  backgroundColor: '#f9fafb',
                  color: '#1e293b',
                  maxHeight: '200px',
                }}
              >
                {formatText(trace.input)}
              </pre>
            </div>

            {/* Output */}
            <div>
              <div
                className="text-xs font-medium mb-1"
                style={{ color: '#6b7280' }}
              >
                Output
              </div>
              <pre
                className="text-xs p-3 rounded border overflow-x-auto whitespace-pre-wrap"
                style={{
                  borderColor: '#e5e7eb',
                  backgroundColor: '#f9fafb',
                  color: '#1e293b',
                  maxHeight: '300px',
                }}
              >
                {formatText(trace.output)}
              </pre>
            </div>

            {/* Observations (steps) */}
            {trace.observations.length > 0 && (
              <div>
                <div
                  className="text-xs font-medium mb-2"
                  style={{ color: '#6b7280' }}
                >
                  Passos da execução ({trace.observations.length})
                </div>
                <div className="space-y-1">
                  {trace.observations.map((obs) => (
                    <div
                      key={obs.id}
                      className="text-xs p-2 rounded border flex justify-between"
                      style={{
                        borderColor: '#e5e7eb',
                        backgroundColor: '#f9fafb',
                      }}
                    >
                      <div>
                        <div
                          className="font-medium"
                          style={{ color: '#1e293b' }}
                        >
                          {obs.name || obs.type}
                        </div>
                        {obs.model && (
                          <div style={{ color: '#6b7280' }}>{obs.model}</div>
                        )}
                      </div>
                      <div className="text-right" style={{ color: '#6b7280' }}>
                        {obs.latencySeconds !== null && (
                          <div>{obs.latencySeconds.toFixed(2)}s</div>
                        )}
                        {obs.costUsd !== null && (
                          <div>{formatUsd(obs.costUsd)}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};
